/*
  GiftGo destination system.

  - Persists the buyer's destination choice in localStorage.
  - Drives the bottom sheet picker (custom element below).
  - Keeps the header pill in sync.
  - Filters product grids by dest:* tags.
  - Renders the delivery promise line, including the live same day countdown,
    on every <delivery-promise> element.

  Vanilla JS, no dependencies. The Liquid in delivery-promise.liquid renders a
  correct no-JS fallback, so everything here is progressive enhancement.
*/
(function () {
  'use strict';

  var CONFIG = window.GiftGoDelivery || {};
  var STORAGE_KEY = CONFIG.storageKey || 'giftgo_destination';
  var DESTS = CONFIG.destinations || [];
  var EXPRESS = CONFIG.express || { min: 2, max: 4 };
  var STANDARD = CONFIG.standard || { min: 7, max: 14 };

  var DEST_BY_CODE = {};
  DESTS.forEach(function (d) {
    DEST_BY_CODE[d.code] = d;
  });

  var CITY = { us: 'US cities', ca: 'Canadian cities', uk: 'UK cities', de: 'German cities' };
  var CODE_LABEL = { us: 'US', uk: 'UK', ca: 'CA', de: 'DE', anywhere: 'All' };

  var currentCode = null;

  /* ----------------------------------------------------------------- utils */
  function getSaved() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function save(code) {
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch (e) {
      /* private mode, ignore */
    }
  }

  function cityPhrase(code) {
    return CITY[code] || 'cities';
  }

  function addDays(n) {
    var d = new Date();
    d.setDate(d.getDate() + n);
    return d;
  }

  function fmtWeekdayDate(d) {
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  function fmtMonthDate(d) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function parseCutoff(str) {
    var m = /^(\d{1,2}):(\d{2})$/.exec((str || '').trim());
    if (!m) return null;
    var h = parseInt(m[1], 10);
    var mi = parseInt(m[2], 10);
    if (h > 23 || mi > 59) return null;
    return { h: h, mi: mi };
  }

  function cutoffLabel(c) {
    var ap = c.h >= 12 ? 'pm' : 'am';
    var hh = c.h % 12;
    if (hh === 0) hh = 12;
    return hh + ':' + String(c.mi).padStart(2, '0') + ap;
  }

  function fmtCountdown(ms) {
    var total = Math.max(0, Math.floor(ms / 1000));
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;
    if (h > 0) return h + 'h ' + m + 'm';
    if (m > 0) return m + 'm ' + s + 's';
    return s + 's';
  }

  /* Clone a flag SVG from the rendered picker options, so we never duplicate
     the SVG markup outside Liquid. */
  function flagHTML(code) {
    var src = document.querySelector('#DestinationPicker [data-destination-select="' + code + '"] .destination-flag');
    return src ? src.innerHTML : '';
  }

  function setFlag(flagEl, code) {
    if (!flagEl) return;
    var html = flagHTML(code);
    if (html) {
      flagEl.innerHTML = html;
      flagEl.className = 'destination-flag destination-flag--' + code;
    }
  }

  /* --------------------------------------------------------------- filtering */
  function matchesDestination(rawTags, code) {
    var tags = (rawTags || '')
      .split(/\s+/)
      .filter(function (t) {
        return t.indexOf('dest:') === 0;
      });
    if (tags.length === 0) return true; // untagged is treated as worldwide
    var hasWorldwide = tags.indexOf('dest:worldwide') !== -1;
    if (code === 'anywhere') return hasWorldwide;
    return hasWorldwide || tags.indexOf('dest:' + code) !== -1;
  }

  function filterGrids(code) {
    document.querySelectorAll('[data-giftgo-dest]').forEach(function (card) {
      var show = matchesDestination(card.getAttribute('data-giftgo-dest'), code);
      var target = card.closest('.grid__item') || card;
      target.classList.toggle('destination-hidden', !show);
    });
  }

  /* ---------------------------------------------------------- promise render */
  function renderPromise(el, code) {
    var speed = el.dataset.speed || 'standard';
    var dest = DEST_BY_CODE[code];
    var line = el.querySelector('[data-promise-line]');
    var countdown = el.querySelector('[data-promise-countdown]');
    var cutoff = dest ? parseCutoff(dest.cutoff) : null;

    var effective = speed;
    if (speed === 'same-day' && !cutoff) effective = 'express'; // not eligible here

    el.classList.remove(
      'delivery-promise--same-day',
      'delivery-promise--express',
      'delivery-promise--standard'
    );
    el.classList.add('delivery-promise--' + effective);

    // Keep the PDP destination header current.
    var nameEl = el.querySelector('[data-promise-dest-name]');
    if (nameEl && dest) nameEl.textContent = dest.label;
    var headFlag = el.querySelector('[data-promise-dest] .destination-flag');
    if (headFlag && dest) setFlag(headFlag, code);

    if (effective === 'same-day') {
      el._giftgoSameDay = { cutoff: cutoff, code: code };
      updateSameDay(el);
      return;
    }

    el._giftgoSameDay = null;
    if (countdown) countdown.hidden = true;

    if (effective === 'express') {
      line.textContent = 'Delivered by ' + fmtWeekdayDate(addDays(EXPRESS.max)) + '.';
    } else {
      line.textContent =
        'Delivered between ' + fmtMonthDate(addDays(STANDARD.min)) + ' and ' + fmtMonthDate(addDays(STANDARD.max)) + '.';
    }
  }

  function updateSameDay(el) {
    var info = el._giftgoSameDay;
    if (!info) return;
    var line = el.querySelector('[data-promise-line]');
    var countdown = el.querySelector('[data-promise-countdown]');
    var now = new Date();
    var target = new Date();
    target.setHours(info.cutoff.h, info.cutoff.mi, 0, 0);
    var phrase = cityPhrase(info.code);

    if (now < target) {
      var left = fmtCountdown(target - now);
      if (el.dataset.variant === 'pdp') {
        line.textContent = 'Delivered today in most ' + phrase + '.';
        if (countdown) {
          countdown.hidden = false;
          countdown.textContent = 'Order in the next ' + left + '.';
        }
      } else {
        line.textContent = 'Order in the next ' + left + ', delivered today in most ' + phrase + '.';
        if (countdown) countdown.hidden = true;
      }
    } else {
      line.textContent =
        'Delivered tomorrow in most ' + phrase + '. Order before ' + cutoffLabel(info.cutoff) + ' for same day.';
      if (countdown) countdown.hidden = true;
    }
  }

  function renderAllPromises(code) {
    document.querySelectorAll('delivery-promise').forEach(function (el) {
      renderPromise(el, code);
    });
  }

  // One global ticker keeps every same day countdown live.
  function startTicker() {
    setInterval(function () {
      document.querySelectorAll('delivery-promise').forEach(function (el) {
        if (el._giftgoSameDay) updateSameDay(el);
      });
    }, 1000);
  }

  /* --------------------------------------------------------------- the pill */
  function updatePill(code) {
    var dest = DEST_BY_CODE[code];
    document.querySelectorAll('[data-destination-pill]').forEach(function (pill) {
      var nameEl = pill.querySelector('[data-pill-name]');
      var codeEl = pill.querySelector('[data-pill-code]');
      var flagEl = pill.querySelector('[data-pill-flag] .destination-flag');
      if (dest) {
        if (nameEl) nameEl.textContent = dest.label;
        if (codeEl) codeEl.textContent = CODE_LABEL[code] || dest.label;
        setFlag(flagEl, code);
      }
    });
  }

  function markSelected() {
    document.querySelectorAll('[data-destination-select]').forEach(function (b) {
      b.classList.toggle('is-selected', b.dataset.destinationSelect === currentCode);
    });
  }

  // Destination-aware line under the hero CTA.
  function updateHeroLine(code) {
    var dest = code ? DEST_BY_CODE[code] : null;
    var text;
    if (!dest) {
      text = 'Choose your destination to see delivery dates.';
    } else if (parseCutoff(dest.cutoff)) {
      text = 'Sending to ' + dest.label + '? Same day delivery available.';
    } else {
      text = 'Sending to ' + dest.label + '. Know the delivery date before you pay.';
    }
    document.querySelectorAll('[data-giftgo-hero-line]').forEach(function (el) {
      el.textContent = text;
    });
  }

  /* ------------------------------------------------------------- apply/select */
  function apply(code) {
    updatePill(code);
    renderAllPromises(code);
    filterGrids(code);
    markSelected();
    updateHeroLine(code);
    document.dispatchEvent(new CustomEvent('giftgo:destination-change', { detail: { code: code } }));
  }

  function select(code) {
    if (!DEST_BY_CODE[code]) return;
    currentCode = code;
    save(code);
    apply(code);
  }
  // Public hook for other components (cart drawer, dynamically loaded sections).
  window.GiftGo = window.GiftGo || {};
  window.GiftGo.getDestination = function () {
    return currentCode;
  };
  window.GiftGo.refresh = function () {
    apply(currentCode || 'anywhere');
  };
  window.GiftGo.openPicker = openPicker;

  function openPicker() {
    var picker = document.querySelector('destination-picker');
    if (picker && typeof picker.open === 'function') picker.open();
  }

  /* ----------------------------------------------------- the custom element */
  class DestinationPicker extends HTMLElement {
    connectedCallback() {
      this.sheet = this.querySelector('.destination-picker__sheet');
      this.overlay = this.querySelector('[data-destination-overlay]');
      this.isOpen = false;

      if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this));
      var closeBtn = this.querySelector('[data-destination-close]');
      if (closeBtn) closeBtn.addEventListener('click', this.close.bind(this));

      this.querySelectorAll('[data-destination-select]').forEach(
        function (btn) {
          btn.addEventListener('click', function () {
            select(btn.dataset.destinationSelect);
            this.close();
          }.bind(this));
        }.bind(this)
      );

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && this.isOpen) this.close();
      }.bind(this));
    }

    open() {
      this.classList.add('destination-picker--open');
      document.documentElement.classList.add('destination-locked');
      this.isOpen = true;
      markSelected();
      if (this.sheet) this.sheet.focus();
    }

    close() {
      this.classList.remove('destination-picker--open');
      document.documentElement.classList.remove('destination-locked');
      this.isOpen = false;
    }
  }

  if (!customElements.get('destination-picker')) {
    customElements.define('destination-picker', DestinationPicker);
  }

  /* --------------------------------------------------------------------- init */
  function init() {
    // Delegate every "open the picker" trigger (header pill, change links).
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-destination-open]');
      if (trigger) {
        e.preventDefault();
        openPicker();
      }
    });

    var saved = getSaved();
    if (saved && DEST_BY_CODE[saved]) {
      currentCode = saved;
      apply(saved);
    } else {
      // No choice yet: show everything, render safe promises, ask once.
      currentCode = null;
      renderAllPromises('anywhere');
      updateHeroLine(null);
      if (CONFIG.autoOpen) {
        var picker = document.querySelector('destination-picker');
        if (picker && typeof picker.open === 'function') picker.open();
      }
    }

    startTicker();

    // Re-apply after Shopify loads/reloads a section (pagination, theme editor).
    document.addEventListener('shopify:section:load', function () {
      apply(currentCode || 'anywhere');
    });

    // Re-render promises when the cart drawer re-renders its line items, so the
    // promise + badge follow each item right up to checkout (the last surface
    // we control on this plan). Dawn publishes 'cart-update' via pubsub.js.
    if (typeof subscribe === 'function') {
      subscribe('cart-update', function () {
        requestAnimationFrame(function () {
          renderAllPromises(currentCode || 'anywhere');
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
